import React, { useState, useEffect } from 'react';
import {
  Card,
  CardContent,
  Typography,
  Box,
  Button,
  Chip,
  Grid,
  CircularProgress,
  Alert,
  IconButton,
  Tooltip
} from '@mui/material';
import {
  Description as DocumentIcon,
  CheckCircle as ProcessedIcon,
  Assignment as ReviewIcon,
  Payment as PaymentIcon,
  Refresh as RefreshIcon,
  Visibility as ViewIcon,
  GetApp as DownloadIcon,
  PlayArrow as ProcessIcon,
  ArrowForward as ArrowForwardIcon,
  ArrowDownward as ArrowDownIcon,
  CloudUpload as CloudUploadIcon,
  Assessment as AnalyticsIcon
} from '@mui/icons-material';
import { invoiceService } from '../../services/invoice.service';

const DashboardTiles = ({ onViewDocuments, onProcessDocuments, onRefresh, onUploadView, onAnalyticsView }) => {
  const [stats, setStats] = useState({
    totalDocuments: 0,
    processedDocuments: 0,
    reviewDocuments: 0,
    readyToPayDocuments: 0,
    rawDocuments: 0
  });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const fetchStats = async () => {
    try {
      setLoading(true);
      setError(null);
      
      const baskets = await invoiceService.getBaskets();
      const documents = await invoiceService.getDocuments();
      
      const basketStats = baskets.baskets.reduce((acc, basket) => {
        acc[basket.name] = basket.document_count;
        return acc;
      }, {});
      
      setStats({
        totalDocuments: documents.total_documents,
        processedDocuments: basketStats.processed_documents || 0,
        reviewDocuments: basketStats.further_validation || 0,
        readyToPayDocuments: basketStats.ready_to_pay || 0,
        rawDocuments: basketStats.raw_invoices || 0
      });
    } catch (err) {
      console.error('Failed to fetch dashboard stats:', err);
      setError('Failed to load dashboard statistics');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchStats();
  }, []);

  const handleRefresh = () => {
    fetchStats();
    if (onRefresh) onRefresh();
  };

  const tiles = [
    {
      title: 'Original Invoices',
      value: stats.rawDocuments,
      icon: <DocumentIcon sx={{ fontSize: 40 }} />,
      color: '#f57c00',
      bgColor: '#fff3e0',
      description: 'Uploaded invoices awaiting processing',
      actions: [
        {
          label: 'View',
          icon: <ViewIcon />,
          onClick: () => onViewDocuments('raw_invoices'),
          color: 'primary'
        },
        {
          label: 'Process All',
          icon: <ProcessIcon />,
          onClick: () => onProcessDocuments('all'),
          color: 'success',
          disabled: stats.rawDocuments === 0
        }
      ]
    },
    {
      title: 'LLM Agent Processed',
      value: stats.processedDocuments,
      icon: <ProcessedIcon sx={{ fontSize: 40 }} />,
      color: '#388e3c',
      bgColor: '#e8f5e8',
      description: 'Processed by AI (success/failure)',
      actions: [
        {
          label: 'View',
          icon: <ViewIcon />,
          onClick: () => onViewDocuments('processed_documents'),
          color: 'primary'
        }
      ]
    },
    {
      title: 'Ready to Pay Invoices',
      value: stats.readyToPayDocuments,
      icon: <PaymentIcon sx={{ fontSize: 40 }} />,
      color: '#7b1fa2',
      bgColor: '#f3e5f5',
      description: 'Successfully processed & approved',
      actions: [
        {
          label: 'View',
          icon: <ViewIcon />,
          onClick: () => onViewDocuments('ready_to_pay'),
          color: 'primary'
        }
      ]
    },
    {
      title: 'Need Reviews',
      value: stats.reviewDocuments,
      icon: <ReviewIcon sx={{ fontSize: 40 }} />,
      color: '#d32f2f',
      bgColor: '#ffebee',
      description: 'Failed processing - needs validation',
      actions: [
        {
          label: 'Review',
          icon: <ViewIcon />,
          onClick: () => onViewDocuments('further_validation'),
          color: 'error'
        }
      ]
    }
  ];

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="200px">
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Alert 
        severity="error" 
        action={
          <Button color="inherit" size="small" onClick={handleRefresh}>
            Retry
          </Button>
        }
      >
        {error}
      </Alert>
    );
  }

  return (
    <Box>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Typography variant="h4" component="h1" gutterBottom>
          Invoice Processing Dashboard
        </Typography>
        <Box display="flex" alignItems="center" gap={1}>
          <Button
            variant="outlined"
            color="primary"
            startIcon={<AnalyticsIcon />}
            onClick={onAnalyticsView}
            sx={{ 
              textTransform: 'none',
              fontWeight: 'medium',
              px: 3
            }}
          >
            Analytics
          </Button>
          <Button
            variant="contained"
            color="primary"
            startIcon={<CloudUploadIcon />}
            onClick={onUploadView}
            sx={{ 
              textTransform: 'none',
              fontWeight: 'medium',
              px: 3
            }}
          >
            Upload Document
          </Button>
          <Tooltip title="Refresh Dashboard">
            <IconButton onClick={handleRefresh} color="primary">
              <RefreshIcon />
            </IconButton>
          </Tooltip>
        </Box>
      </Box>

      {/* First Row: Original Invoices -> LLM Agent Processed -> Ready to Pay Invoices */}
      <Grid container spacing={1} sx={{ mb: 2 }}>
        {tiles.slice(0, 3).map((tile, index) => (
          <React.Fragment key={index}>
            <Grid item xs={12} sm={6} lg={3.6}>
              <Card 
                sx={{ 
                  height: '100%',
                  display: 'flex',
                  flexDirection: 'column',
                  transition: 'transform 0.2s ease-in-out, box-shadow 0.2s ease-in-out',
                  '&:hover': {
                    transform: 'translateY(-4px)',
                    boxShadow: 4
                  }
                }}
              >
                <CardContent sx={{ flexGrow: 1, p: 2.5 }}>
                  <Box display="flex" alignItems="center" justifyContent="space-between" mb={1.5}>
                    <Box
                      sx={{
                        p: 1.5,
                        borderRadius: 2,
                        backgroundColor: tile.bgColor,
                        color: tile.color
                      }}
                    >
                      {tile.icon}
                    </Box>
                    <Chip 
                      label={tile.value} 
                      color="primary" 
                      variant="outlined"
                      sx={{ fontWeight: 'bold', fontSize: '1rem' }}
                    />
                  </Box>
                  
                  <Typography variant="h6" component="h2" gutterBottom sx={{ fontSize: '1.1rem' }}>
                    {tile.title}
                  </Typography>
                  
                  <Typography variant="body2" color="text.secondary" mb={1.5} sx={{ fontSize: '0.85rem' }}>
                    {tile.description}
                  </Typography>

                  <Box display="flex" flexDirection="column" gap={0.5}>
                    {tile.actions.map((action, actionIndex) => (
                      <Button
                        key={actionIndex}
                        variant={actionIndex === 0 ? "contained" : "outlined"}
                        size="small"
                        startIcon={action.icon}
                        onClick={action.onClick}
                        disabled={action.disabled}
                        color={action.color}
                        fullWidth
                        sx={{ 
                          textTransform: 'none',
                          fontSize: '0.8rem',
                          py: 0.5
                        }}
                      >
                        {action.label}
                      </Button>
                    ))}
                  </Box>
                </CardContent>
              </Card>
            </Grid>
            {index < 2 && (
              <Grid item xs={12} sm={6} lg={0.6} sx={{ display: { xs: 'none', lg: 'flex' }, alignItems: 'center', justifyContent: 'center' }}>
                <ArrowForwardIcon sx={{ fontSize: 28, color: 'primary.main' }} />
              </Grid>
            )}
          </React.Fragment>
        ))}
      </Grid>

      {/* Second Row: Need Reviews (aligned with Ready to Pay) with bracket arrow from LLM Agent */}
      <Grid container spacing={1}>
        {/* Spacer to align with Original Invoices tile */}
        <Grid item xs={12} sm={6} lg={3.6} sx={{ display: { xs: 'none', lg: 'block' }}}>
        </Grid>
        
        {/* Spacer to align with first arrow */}
        <Grid item xs={12} sm={6} lg={0.6} sx={{ display: { xs: 'none', lg: 'block' }}}>
        </Grid>
        
        {/* Spacer to align with LLM Agent Processed tile */}
        <Grid item xs={12} sm={6} lg={3.6} sx={{ display: { xs: 'none', lg: 'block' }}}>
        </Grid>
        
        {/* Spacer to align with second arrow */}
        <Grid item xs={12} sm={6} lg={0.6} sx={{ display: { xs: 'none', lg: 'block' }}}>
        </Grid>
        
        {/* Need Reviews tile - aligned with Ready to Pay */}
        <Grid item xs={12} sm={6} lg={3.6}>
          {/* Red arrow positioned to align with Need Reviews tile and second blue arrow */}
          <Box 
            sx={{ 
              position: 'relative',
              display: { xs: 'none', lg: 'block' },
              mb: 1.5
            }}
          >
            <Box
              sx={{
                position: 'absolute',
                top: 0,
                left: -60,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center'
              }}
            >
              <ArrowForwardIcon sx={{ fontSize: 28, color: 'error.main' }} />
            </Box>
          </Box>
          
          <Card 
            sx={{ 
              height: '100%',
              display: 'flex',
              flexDirection: 'column',
              transition: 'transform 0.2s ease-in-out, box-shadow 0.2s ease-in-out',
              '&:hover': {
                transform: 'translateY(-4px)',
                boxShadow: 4
              }
            }}
          >
            <CardContent sx={{ flexGrow: 1, p: 2.5 }}>
              <Box display="flex" alignItems="center" justifyContent="space-between" mb={1.5}>
                <Box
                  sx={{
                    p: 1.5,
                    borderRadius: 2,
                    backgroundColor: tiles[3].bgColor,
                    color: tiles[3].color
                  }}
                >
                  {tiles[3].icon}
                </Box>
                <Chip 
                  label={tiles[3].value} 
                  color="primary" 
                  variant="outlined"
                  sx={{ fontWeight: 'bold', fontSize: '1rem' }}
                />
              </Box>
              
              <Typography variant="h6" component="h2" gutterBottom sx={{ fontSize: '1.1rem' }}>
                {tiles[3].title}
              </Typography>
              
              <Typography variant="body2" color="text.secondary" mb={1.5} sx={{ fontSize: '0.85rem' }}>
                {tiles[3].description}
              </Typography>

              <Box display="flex" flexDirection="column" gap={0.5}>
                {tiles[3].actions.map((action, actionIndex) => (
                  <Button
                    key={actionIndex}
                    variant={actionIndex === 0 ? "contained" : "outlined"}
                    size="small"
                    startIcon={action.icon}
                    onClick={action.onClick}
                    disabled={action.disabled}
                    color={action.color}
                    fullWidth
                    sx={{ 
                      textTransform: 'none',
                      fontSize: '0.8rem',
                      py: 0.5
                    }}
                  >
                    {action.label}
                  </Button>
                ))}
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    </Box>
  );
};

export default DashboardTiles;
